import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601922046210-41e129a3e64a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const META = [
  "Limited numbered editions",
  "Delivered by post",
  "Played at home",
  "No screens required",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const rx = useSpring(0, { stiffness: 60, damping: 16 });
  const ry = useSpring(0, { stiffness: 60, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 9);
    rx.set(-py * 9);
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
    >
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 sm:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.15}>
            <span
              data-testid="hero-overline"
              className="mb-8 inline-block font-cinzel text-[11px] tracking-[0.45em] text-gold/80"
            >
              POST-DELIVERED IMMERSIVE THEATRE
            </span>
          </MaskedLine>
          <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-cream sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.3}>Immersive</MaskedLine>
            <MaskedLine delay={0.42}>
              <em className="font-normal italic text-gold">at Home.</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.62}>
            <p
              data-testid="hero-subtitle"
              className="mt-8 max-w-md text-base leading-relaxed text-sand sm:text-lg"
            >
              Stories you can hold, sealed in wax and sent by post. Each episode
              is a box of theatre — letters, artifacts and an audio companion —
              played out in your own rooms, by candlelight.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-episode-button"
              onClick={() => scrollToId("#episode")}
              className="group rounded-full bg-gold px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-obsidian transition-all duration-300 hover:bg-gold-glow hover:shadow-[0_0_40px_rgba(226,177,93,0.35)]"
            >
              Meet Episode One
            </button>
            <button
              data-testid="hero-join-button"
              onClick={() => scrollToId("#join")}
              className="rounded-full border border-cream/20 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-cream transition-colors duration-300 hover:border-gold/60 hover:text-gold"
            >
              Join the ledger
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.15 }}
            className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          >
            {META.map((item, i) => (
              <span
                key={item}
                className="flex items-center gap-8 text-[11px] uppercase tracking-[0.2em] text-ash"
              >
                {item}
                {i < META.length - 1 && (
                  <span className="hidden h-1 w-1 rounded-full bg-gold/40 sm:inline-block" />
                )}
              </span>
            ))}
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)", opacity: 0.4 }}
            animate={{ clipPath: "inset(0% 0 0 0)", opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onMouseMove={onTilt}
            onMouseLeave={() => {
              rx.set(0);
              ry.set(0);
            }}
            className="relative mx-auto max-w-sm"
            style={{ transformPerspective: 900 }}
          >
            <motion.div style={{ rotateX: rx, rotateY: ry, y: parallaxY }}>
              <div className="overflow-hidden rounded-t-[12rem] border border-gold/20 shadow-[0_40px_120px_rgba(0,0,0,0.7)]">
                <img
                  data-testid="hero-keyart-image"
                  src={HERO_IMG}
                  alt="Candlelight in a darkened parlour"
                  className="aspect-[3/4] w-full scale-105 object-cover"
                />
              </div>
              <motion.div
                initial={{ x: "-50%" }}
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 left-1/2 z-10 w-max border border-gold/25 bg-surface/95 px-6 py-4 text-center backdrop-blur-sm"
              >
                <p className="font-cinzel text-[10px] tracking-[0.3em] text-gold">
                  EPISODE ONE — THE SÉANCE
                </p>
                <p className="mt-1 font-serif text-lg italic text-cream">
                  Now showing — wax sealed
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
